// ─── Diagnostic Quiz ─────────────────────────────────────────────────────────
const DiagQuiz = {
  card: null,
  data: [], // raw areas array
  flatQ: [], // flattened questions
  answers: [], // score per flat index (null = not answered)
  currentIndex: 0,
  thresholdCritico: 30,
  thresholdSaudavel: 70,

  init() {
    this.card = document.getElementById("diagCard");

    if (!this.card) {
      return;
    }

    try {
      this.data = JSON.parse(this.card.dataset.quiz);
    } catch (e) {
      console.warn("DiagQuiz: invalid data", e);
      return;
    }

    this.thresholdCritico =
      parseFloat(this.card.dataset.thresholdCritico) || 30;
    this.thresholdSaudavel =
      parseFloat(this.card.dataset.thresholdSaudavel) || 70;

    this._buildFlat();
    this._renderQuestion(0);
    this._bindEvents();
  },

  // ── Categoriza uma % com base nos thresholds do ACF ───────────────────────────
  _categorize(pct) {
    if (pct <= this.thresholdCritico) {
      return { status: "Crítico", cls: "is-critico", severity: "Grave" };
    }

    if (pct >= this.thresholdSaudavel) {
      return { status: "Saudável", cls: "is-saudavel", severity: "Leve" };
    }

    return { status: "Atenção", cls: "is-atencao", severity: "Moderada" };
  },

  _buildFlat() {
    this.flatQ = [];
    this.data.forEach((area, aIdx) => {
      area.questions.forEach((q, qIdx) => {
        const maxScore = q.options.reduce((m, o) => Math.max(m, o.score), 0);
        this.flatQ.push({
          areaIndex: aIdx,
          areaName: area.area,
          areaChartName: area.areaChart || area.area,
          trail: area.trail || "",
          questionIndexInArea: qIdx + 1,
          totalInArea: area.questions.length,
          question: q.question,
          options: q.options,
          maxScore,
        });
      });
    });
    this.answers = new Array(this.flatQ.length).fill(null);
  },

  _bindEvents() {
    document
      .getElementById("diagBack")
      ?.addEventListener("click", () => this._goBack());
    document
      .getElementById("diagAreaBtn")
      ?.addEventListener("click", () => this._showAreas());
    document
      .getElementById("diagAreasBack")
      ?.addEventListener("click", () => this._showResults());
    document
      .getElementById("diagAreasNext")
      ?.addEventListener("click", () => this._showTrail());
    document
      .getElementById("diagTrailBack")
      ?.addEventListener("click", () => this._showAreas());
    document
      .getElementById("diagTrailReset")
      ?.addEventListener("click", () => this._resetQuiz());
  },

  // ── Render ────────────────────────────────────────────────────────────────────
  _renderQuestion(index) {
    const question = this.flatQ[index];

    if (!question) {
      return;
    }

    const backBtn = document.getElementById("diagBack");
    const progressEl = document.getElementById("diagProgress");
    const questionEl = document.getElementById("diagQuestion");
    const optionsEl = document.getElementById("diagOptions");

    if (backBtn) {
      backBtn.style.display = index > 0 ? "flex" : "none";
    }

    if (progressEl) {
      const num = String(question.questionIndexInArea).padStart(2, "0");
      const total = String(question.totalInArea).padStart(2, "0");
      progressEl.innerHTML =
        `Pergunta <span>${num}</span> ` +
        `<span class="prog-de">de </span>` +
        `<span>${total} sobre </span>` +
        `<span class="prog-area">${question.areaName}</span>`;
    }

    if (questionEl) {
      questionEl.textContent = question.question;
    }

    if (optionsEl) {
      optionsEl.innerHTML = "";
      question.options.forEach((opt, i) => {
        const btn = document.createElement("button");
        btn.className = "diag-card__option";
        const stepLetter = String.fromCharCode(65 + i);

        btn.innerHTML = `<span class="sans3-20-semibold">${stepLetter}.</span> ${opt.label}`;
        btn.addEventListener("click", () =>
          this._selectOption(index, opt.score),
        );

        optionsEl.appendChild(btn);
      });
    }
  },

  // ── Navigation ───────────────────────────────────────────────────────────────
  async _selectOption(index, score) {
    this.answers[index] = score;
    const next = index + 1;

    if (next >= this.flatQ.length) {
      await this._transitionToResults();
    } else {
      this.currentIndex = next;
      await this._animateStep(() => this._renderQuestion(next));
    }
  },

  async _goBack() {
    if (this.currentIndex <= 0) {
      return;
    }

    this.currentIndex--;

    for (let i = this.currentIndex; i < this.flatQ.length; i++) {
      this.answers[i] = null;
    }

    await this._animateStep(() => this._renderQuestion(this.currentIndex));
  },

  async _animateStep(renderFn) {
    const step = document.getElementById("diagStep");

    if (!step) {
      renderFn();
      return;
    }

    step.classList.add("fade-out");
    await this._sleep(230);
    renderFn();
    step.classList.remove("fade-out");
    step.classList.add("fade-in");
    step.offsetHeight; // force reflow
    step.classList.remove("fade-in");
  },

  // ── Results ──────────────────────────────────────────────────────────────────
  async _transitionToResults() {
    let userScore = 0;
    let maxScore = 0;

    this.flatQ.forEach((q, i) => {
      userScore += this.answers[i] ?? 0;
      maxScore += q.maxScore;
    });

    const pct = maxScore > 0 ? (userScore / maxScore) * 100 : 0;
    const { severity } = this._categorize(pct);

    const scoreEl = document.getElementById("diagScore");
    const severityEl = document.getElementById("diagSeverity");
    const percentEl = document.getElementById("diagPercent");

    if (scoreEl) {
      scoreEl.textContent = `${userScore}/${maxScore}`;
    }

    if (severityEl) {
      severityEl.textContent = severity;
    }

    if (percentEl) {
      percentEl.textContent = `${Math.round(pct)}% dos indicadores`;
    }

    const quizEl = document.getElementById("diagQuiz");
    const resultsEl = document.getElementById("diagResults");

    if (!quizEl || !resultsEl) {
      return;
    }

    quizEl.style.transition = "opacity 0.22s ease";
    quizEl.style.opacity = "0";
    await this._sleep(230);
    quizEl.style.display = "none";

    resultsEl.style.display = "flex";
    resultsEl.style.opacity = "0";
    resultsEl.style.transition = "opacity 0.22s ease";
    resultsEl.offsetHeight;
    resultsEl.style.opacity = "1";
  },

  _showResults() {
    const areasEl = document.getElementById("diagAreas");
    const resultsEl = document.getElementById("diagResults");

    if (areasEl) {
      areasEl.style.display = "none";
    }

    if (resultsEl) {
      resultsEl.style.display = "flex";
    }
  },

  // ── Areas ────────────────────────────────────────────────────────────────────
  _showAreas() {
    const resultsEl = document.getElementById("diagResults");
    const areasEl = document.getElementById("diagAreas");
    const gridEl = document.getElementById("diagAreasGrid");

    if (!gridEl) {
      return;
    }

    const areaMap = {};
    this.flatQ.forEach((q, i) => {
      if (!areaMap[q.areaIndex]) {
        areaMap[q.areaIndex] = {
          name: q.areaChartName,
          trail: q.trail,
          score: 0,
          max: 0,
        };
      }

      areaMap[q.areaIndex].score += this.answers[i] ?? 0;
      areaMap[q.areaIndex].max += q.maxScore;
    });

    gridEl.innerHTML = "";
    Object.values(areaMap).forEach(({ name, trail, score, max }) => {
      const pct = max > 0 ? (score / max) * 100 : 0;
      const { status, cls } = this._categorize(pct);

      const item = document.createElement("div");
      item.className = "diag-areas__item";
      item.innerHTML =
        `<span class="sora-16 diag-areas__area-name">${name}</span>` +
        `<span class="diag-areas__gauge">${this._gaugeSvg(status)}</span>` +
        `<span class="sora-16 diag-areas__status ${cls}">${status}</span>`;
      gridEl.appendChild(item);
    });

    if (resultsEl) {
      resultsEl.style.display = "none";
    }

    if (areasEl) {
      areasEl.style.display = "flex";
    }
  },

  // ── Trail ────────────────────────────────────────────────────────────────────
  _showTrail() {
    const areasEl = document.getElementById("diagAreas");
    const trailEl = document.getElementById("diagTrail");
    const sobreEl = document.getElementById("diagTrailSobre");

    if (sobreEl) {
      // Determina a área com pior score para exibir a trilha correspondente
      const areaScores = {};

      this.flatQ.forEach((q, i) => {
        if (!areaScores[q.areaIndex]) {
          areaScores[q.areaIndex] = { trail: q.trail, score: 0, max: 0 };
        }
        areaScores[q.areaIndex].score += this.answers[i] ?? 0;
        areaScores[q.areaIndex].max += q.maxScore;
      });

      let worstTrail = "";
      let worstPct = -1;

      Object.values(areaScores).forEach(({ trail, score, max }) => {
        const pct = max > 0 ? (score / max) * 100 : 0;
        if (pct > worstPct) {
          worstPct = pct;
          worstTrail = trail;
        }
      });

      sobreEl.innerHTML = worstTrail;
    }

    if (areasEl) {
      areasEl.style.display = "none";
    }

    if (trailEl) {
      trailEl.style.display = "flex";
    }
  },

  _resetQuiz() {
    this.answers.fill(null);
    this.currentIndex = 0;

    const trailEl = document.getElementById("diagTrail");
    const areasEl = document.getElementById("diagAreas");
    const resultsEl = document.getElementById("diagResults");
    const quizEl = document.getElementById("diagQuiz");

    if (trailEl) {
      trailEl.style.display = "none";
    }

    if (areasEl) {
      areasEl.style.display = "none";
    }

    if (resultsEl) {
      resultsEl.style.display = "none";
      resultsEl.style.opacity = "1";
    }

    if (quizEl) {
      quizEl.style.display = "flex";
      quizEl.style.opacity = "1";
    }

    this._renderQuestion(0);
  },

  // ── Gauge SVG por status ──────────────────────────────────────────────────────
  _gaugeSvg(status) {
    const svgs = {
      Saudável: `<svg style="width:100%;height:auto" viewBox="0 0 173 82" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M159.85 81.5C164.351 81.5 168.042 77.842 167.593 73.3634C166.5 62.4702 163.22 51.8796 157.919 42.2371C152.618 32.5946 145.434 24.1506 136.822 17.3909C133.281 14.6117 128.215 15.7681 125.803 19.5685L117.069 33.3311C114.657 37.1315 115.841 42.1225 119.188 45.1313C123.257 48.7881 126.698 53.1149 129.351 57.9422C132.005 62.7696 133.815 67.9931 134.722 73.3876C135.469 77.8263 139.049 81.5 143.55 81.5H159.85Z" fill="#17B26A"/><path d="M121.98 17.3021C124.157 13.3626 122.741 8.36262 118.604 6.58955C108.541 2.27697 97.6858 0.0248039 86.6822 0.000203676C75.6787 -0.0243966 64.813 2.17921 54.7311 6.44675C50.586 8.2013 49.1476 13.1949 51.3072 17.1441L59.1278 31.4454C61.2874 35.3946 66.2282 36.7728 70.4809 35.2983C75.6494 33.5063 81.1005 32.5878 86.6093 32.6001C92.1181 32.6124 97.5651 33.5553 102.726 35.3704C106.972 36.8639 111.919 35.5078 114.096 31.5683L121.98 17.3021Z" fill="#F79009"/><path d="M47.0065 19.6899C44.583 15.897 39.5131 14.7561 35.981 17.5462C27.3901 24.3324 20.2317 32.7985 14.9604 42.4573C9.6891 52.1161 6.44189 62.7167 5.38247 73.6133C4.9469 78.0933 8.64925 81.7399 13.1503 81.726L29.4503 81.6758C33.9514 81.6619 37.5198 77.9772 38.2528 73.5362C39.1437 68.1389 40.9372 62.9099 43.5762 58.0744C46.2152 53.2388 49.6425 48.9015 53.6997 45.2322C57.038 42.213 58.2063 37.2185 55.7828 33.4255L47.0065 19.6899Z" fill="#F04438"/><circle cx="86" cy="72" r="10" fill="#FAF9F5"/><path d="M113.696 61.7748C115.239 62.0841 115.837 63.9718 114.753 65.1129L106.012 74.3188C104.963 75.423 103.115 74.9972 102.655 73.5455L98.949 61.8442C98.4893 60.3925 99.7558 58.98 101.249 59.2793L113.696 61.7748Z" fill="#FAF9F5"/></svg>`,
      Atenção: `<svg style="width:100%;height:auto" viewBox="0 0 163 82" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M154.85 81.5C159.351 81.5 163.042 77.842 162.593 73.3634C161.5 62.4702 158.22 51.8796 152.919 42.2371C147.618 32.5946 140.434 24.1506 131.822 17.3909C128.281 14.6117 123.215 15.7681 120.803 19.5685L112.069 33.3311C109.657 37.1315 110.841 42.1225 114.188 45.1313C118.257 48.7881 121.698 53.1149 124.351 57.9422C127.005 62.7696 128.815 67.9931 129.722 73.3876C130.469 77.8263 134.049 81.5 138.55 81.5H154.85Z" fill="#17B26A"/><path d="M116.98 17.3021C119.157 13.3626 117.741 8.36262 113.604 6.58955C103.541 2.27697 92.6858 0.0248039 81.6822 0.000203676C70.6787 -0.0243966 59.813 2.17921 49.7311 6.44675C45.586 8.2013 44.1476 13.1949 46.3072 17.1441L54.1278 31.4454C56.2874 35.3946 61.2282 36.7728 65.4809 35.2983C70.6494 33.5063 76.1005 32.5878 81.6093 32.6001C87.1181 32.6124 92.5651 33.5553 97.7255 35.3704C101.972 36.8639 106.919 35.5078 109.096 31.5683L116.98 17.3021Z" fill="#F79009"/><path d="M42.0065 19.6899C39.583 15.897 34.5131 14.7561 30.981 17.5462C22.3901 24.3324 15.2317 32.7985 9.96037 42.4573C4.6891 52.1161 1.44189 62.7167 0.38247 73.6133C-0.0530985 78.0933 3.64925 81.7399 8.15035 81.726L24.4503 81.6758C28.9514 81.6619 32.5198 77.9772 33.2528 73.5362C34.1437 68.1389 35.9372 62.9099 38.5762 58.0744C41.2152 53.2388 44.6425 48.9015 48.6997 45.2322C52.038 42.213 53.2063 37.2185 50.7828 33.4255L42.0065 19.6899Z" fill="#F04438"/><circle cx="81" cy="72" r="10" fill="#FAF9F5"/><path d="M79.2492 43.9925C80.01 42.615 81.99 42.615 82.7508 43.9925L88.8878 55.1054C89.624 56.4384 88.6598 58.0723 87.1371 58.0723H74.8629C73.3402 58.0723 72.376 56.4384 73.1122 55.1054L79.2492 43.9925Z" fill="#FAF9F5"/></svg>`,
      Crítico: `<svg style="width:100%;height:auto" viewBox="0 0 163 82" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M154.85 81.5C159.351 81.5 163.042 77.842 162.593 73.3634C161.5 62.4702 158.22 51.8796 152.919 42.2371C147.618 32.5946 140.434 24.1506 131.822 17.3909C128.281 14.6117 123.215 15.7681 120.803 19.5685L112.069 33.3311C109.657 37.1315 110.841 42.1225 114.188 45.1313C118.257 48.7881 121.698 53.1149 124.351 57.9422C127.005 62.7696 128.815 67.9931 129.722 73.3876C130.469 77.8263 134.049 81.5 138.55 81.5H154.85Z" fill="#17B26A"/><path d="M116.98 17.3021C119.157 13.3626 117.741 8.36262 113.604 6.58955C103.541 2.27697 92.6858 0.0248039 81.6822 0.000203676C70.6787 -0.0243966 59.813 2.17921 49.7311 6.44675C45.586 8.2013 44.1476 13.1949 46.3072 17.1441L54.1278 31.4454C56.2874 35.3946 61.2282 36.7728 65.4809 35.2983C70.6494 33.5063 76.1005 32.5878 81.6093 32.6001C87.1181 32.6124 92.5651 33.5553 97.7255 35.3704C101.972 36.8639 106.919 35.5078 109.096 31.5683L116.98 17.3021Z" fill="#F79009"/><path d="M42.0065 19.6899C39.583 15.897 34.5131 14.7561 30.981 17.5462C22.3901 24.3324 15.2317 32.7985 9.96037 42.4573C4.6891 52.1161 1.44189 62.7167 0.38247 73.6133C-0.0530985 78.0933 3.64925 81.7399 8.15035 81.726L24.4503 81.6758C28.9514 81.6619 32.5198 77.9772 33.2528 73.5362C34.1437 68.1389 35.9372 62.9099 38.5762 58.0744C41.2152 53.2388 44.6425 48.9015 48.6997 45.2322C52.038 42.213 53.2063 37.2185 50.7828 33.4255L42.0065 19.6899Z" fill="#F04438"/><circle cx="81" cy="72" r="10" fill="#FAF9F5"/><path d="M52.5887 65.282C51.5206 64.1263 52.144 62.2469 53.6909 61.9584L66.1707 59.6317C67.6677 59.3526 68.915 60.7821 68.4356 62.2274L64.5718 73.8775C64.0925 75.3228 62.2381 75.7236 61.2046 74.6053L52.5887 65.282Z" fill="#FAF9F5"/></svg>`,
    };
    return svgs[status] || svgs["Saudável"];
  },

  _sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
};

// ─── FAQ Accordion ───────────────────────────────────────────────────────────
function initFaq() {
  const items = document.querySelectorAll("[data-faq-item]");

  if (!items.length) {
    return;
  }

  items.forEach((item) => {
    const btn = item.querySelector(".home-faq__question");

    if (!btn) {
      return;
    }

    btn.addEventListener("click", () => {
      const isOpen = item.classList.contains("is-open");

      items.forEach((item) => {
        item.classList.remove("is-open");
        item
          .querySelector(".home-faq__question")
          ?.setAttribute("aria-expanded", "false");
      });

      if (!isOpen) {
        item.classList.add("is-open");
        btn.setAttribute("aria-expanded", "true");
      }
    });
  });
}

// ─── Video Facade ─────────────────────────────────────────────────────────────
function initVideoFacade() {
  document.querySelectorAll(".home-quem-somos__video-wrap").forEach((wrap) => {
    wrap.addEventListener(
      "click",
      () => {
        const url = wrap.dataset.ytUrl || "";
        const urlMatch = url.match(
          /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?#]+)/,
        );

        if (!urlMatch?.[1]) {
          return;
        }

        const iframe = document.createElement("iframe");
        iframe.src = `https://www.youtube.com/embed/${urlMatch[1]}?autoplay=1`;
        iframe.setAttribute(
          "allow",
          "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
        );
        iframe.setAttribute("allowfullscreen", "");
        iframe.className = "home-quem-somos__iframe";
        wrap.replaceChildren(iframe);
        wrap.style.cursor = "default";
      },
      { once: true },
    );
  });
}

// ─── Clínicas – vídeo por card ────────────────────────────────────────────────
function initClinicasVideos() {
  document
    .querySelectorAll(".home-clinicas__card[data-yt-url]")
    .forEach((card) => {
      const btn = card.querySelector(".home-clinicas__card-play");

      if (!btn) {
        return;
      }

      btn.addEventListener(
        "click",
        () => {
          const url = card.dataset.ytUrl || "";
          const urlMatch = url.match(
            /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?#]+)/,
          );

          if (!urlMatch?.[1]) {
            return;
          }

          const iframe = document.createElement("iframe");
          iframe.src = `https://www.youtube.com/embed/${urlMatch[1]}?autoplay=1`;
          iframe.setAttribute(
            "allow",
            "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
          );
          iframe.setAttribute("allowfullscreen", "");
          iframe.className = "home-clinicas__card-iframe";
          card.replaceChildren(iframe);
          card.style.cursor = "default";
        },
        { once: true },
      );
    });
}

// ─── Forminator: Diagnóstico ──────────────────────────────────────────────────
function initDiagnosticoForm() {
  function waitFor(selector, cb) {
    const el = document.body.querySelector(selector);

    if (el) {
      cb(el);
      return;
    }

    const obs = new MutationObserver(() => {
      const el = document.body.querySelector(selector);

      if (el) {
        obs.disconnect();
        cb(el);
      }
    });

    obs.observe(document.body, { childList: true, subtree: true });
  }

  waitFor(
    ".forminator-custom-form-192 .forminator-pagination[data-step='0']",
    (step0) => {
      const form = step0.closest(".forminator-custom-form-192");

      if (form) {
        setupForm(form);
      }
    },
  );

  function setupForm(form) {
    const SVG_PERSON = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.9998 21.0035C16.9725 21.0035 21.0036 16.9724 21.0036 11.9998C21.0036 7.02715 16.9725 2.99603 11.9998 2.99603C7.02721 2.99603 2.99609 7.02715 2.99609 11.9998C2.99609 16.9724 7.02721 21.0035 11.9998 21.0035Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M12.0025 6.74805H12C10.3431 6.74805 9 8.09119 9 9.74805V10.2508C9 11.9076 10.3431 13.2508 12 13.2508H12.0025C13.6594 13.2508 15.0025 11.9076 15.0025 10.2508V9.74805C15.0025 8.09119 13.6594 6.74805 12.0025 6.74805Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M5.63281 18.3669C7.14702 16.8527 9.20074 16.002 11.3422 16.002H12.5557C14.1499 16.002 15.7083 16.4741 17.0346 17.3585L18.4352 18.2909" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    const SVG_EMAIL = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 4.5H7C4.79086 4.5 3 6.29086 3 8.5V15.5C3 17.7091 4.79086 19.5 7 19.5H17C19.2091 19.5 21 17.7091 21 15.5V8.5C21 6.29086 19.2091 4.5 17 4.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M7 8.98376L11.2046 10.817C11.7124 11.0384 12.2893 11.0393 12.7978 10.8194L17 9.00239" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    const SVG_PHONE = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.8542 13.144C9.68419 11.974 8.80219 10.664 8.21619 9.33499C8.09219 9.05399 8.16519 8.72499 8.38219 8.50799L9.20119 7.68999C9.87219 7.01899 9.87219 6.06999 9.28619 5.48399L8.11219 4.30999C7.33119 3.52899 6.06519 3.52899 5.28419 4.30999L4.63219 4.96199C3.89119 5.70299 3.58219 6.77199 3.78219 7.83199C4.27619 10.445 5.79419 13.306 8.24319 15.755C10.6922 18.204 13.5532 19.722 16.1662 20.216C17.2262 20.416 18.2952 20.107 19.0362 19.366L19.6872 18.715C20.4682 17.934 20.4682 16.668 19.6872 15.887L18.5142 14.714C17.9282 14.128 16.9782 14.128 16.3932 14.714L15.4902 15.618C15.2732 15.835 14.9442 15.908 14.6632 15.784C13.3342 15.197 12.0242 14.314 10.8542 13.144Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    function makeInputWrap(input, svgStr) {
      const wrap = document.createElement("div");
      wrap.className = "form-input-wrap";
      const icon = document.createElement("span");
      icon.className = "form-input-icon";
      icon.innerHTML = svgStr;
      wrap.appendChild(icon);
      wrap.appendChild(input);
      return wrap;
    }

    // ── Step 0: Nome ─────────────────────────────────────────────────────────────
    // .forminator-field-name é o col externo; o .forminator-field interno tem label/input diretos
    const nameField = form.querySelector(
      ".forminator-field-name .forminator-field",
    );
    console.log("nameField:", nameField);
    if (nameField) {
      const label = nameField.querySelector(":scope > label.forminator-label");
      const input = nameField.querySelector(":scope > input.forminator-input");
      const descDiv = nameField.querySelector(":scope > div");

      if (label && input) {
        const heading = document.createElement("p");
        heading.className = "form-step-heading";
        heading.textContent = "Nos informe seu nome";

        const card = document.createElement("div");
        card.className = "form-input-card";
        card.appendChild(label);
        card.appendChild(makeInputWrap(input, SVG_PERSON));

        while (nameField.firstChild)
          nameField.removeChild(nameField.firstChild);
        if (descDiv) nameField.appendChild(descDiv);
        nameField.appendChild(heading);
        nameField.appendChild(card);
      }
    }

    // ── Steps 1 e 2: Radio ───────────────────────────────────────────────────────
    [1, 2].forEach((n) => {
      const radioField = form.querySelector(
        `.forminator-pagination[data-step='${n}'] .forminator-field[role='radiogroup']`,
      );
      if (!radioField) return;

      const spanLabel = radioField.querySelector(
        ":scope > span.forminator-label",
      );
      const descDiv = radioField.querySelector(":scope > div");
      const radios = Array.from(
        radioField.querySelectorAll(":scope > label.forminator-radio"),
      );

      const card = document.createElement("div");
      card.className = "form-radio-card";
      radios.forEach((r) => card.appendChild(r));

      while (radioField.firstChild)
        radioField.removeChild(radioField.firstChild);
      if (descDiv) radioField.appendChild(descDiv);
      if (spanLabel) radioField.appendChild(spanLabel);
      radioField.appendChild(card);
    });

    // ── Step 3: Email + Phone em um único card ───────────────────────────────────
    const step3Content = form.querySelector(
      ".forminator-pagination[data-step='3'] .forminator-pagination--content",
    );
    if (step3Content) {
      const emailField =
        step3Content.querySelector(
          ".forminator-field-email .forminator-field",
        ) ||
        step3Content
          .querySelector("input.forminator-email--field, input[type='email']")
          ?.closest(".forminator-field");
      const phoneField =
        step3Content.querySelector(
          ".forminator-field-phone .forminator-field",
        ) ||
        step3Content
          .querySelector("input.forminator-field--phone")
          ?.closest(".forminator-field");

      if (emailField && phoneField) {
        const emailLabel = emailField.querySelector(
          ":scope > label.forminator-label",
        );
        const emailInput = emailField.querySelector(
          ":scope > input.forminator-input",
        );
        const emailDescDiv = emailField.querySelector(":scope > div");
        const phoneLabel = phoneField.querySelector(
          ":scope > label.forminator-label",
        );
        const phoneInput = phoneField.querySelector(
          ":scope > input.forminator-input",
        );

        const heading = document.createElement("p");
        heading.className = "form-step-heading";
        heading.textContent = "Nos informe seus dados de contato";

        const card = document.createElement("div");
        card.className = "form-input-card";
        if (emailLabel) card.appendChild(emailLabel);
        card.appendChild(makeInputWrap(emailInput, SVG_EMAIL));
        if (phoneLabel) card.appendChild(phoneLabel);
        card.appendChild(makeInputWrap(phoneInput, SVG_PHONE));

        const submitBtn = step3Content.querySelector(
          ".forminator-pagination-submit",
        );
        step3Content
          .querySelectorAll(".forminator-row")
          .forEach((r) => r.remove());

        const anchor = submitBtn || null;
        if (emailDescDiv) step3Content.insertBefore(emailDescDiv, anchor);
        step3Content.insertBefore(heading, anchor);
        step3Content.insertBefore(card, anchor);
      }
    }

    // ── Auto-avançar nos steps de radio ──────────────────────────────────────────
    form.querySelectorAll("input[type='radio']").forEach((radio) => {
      radio.addEventListener("change", () => {
        setTimeout(() => {
          form.querySelector(".forminator-button-next")?.click();
        }, 450);
      });
    });

    // ── Ocultar botões conforme o step ───────────────────────────────────────────
    function updateNextBtn() {
      if (form.dataset.submitted) {
        return;
      }

      const active = form.querySelector(".forminator-pagination:not([hidden])");

      if (!active) {
        return;
      }

      const step = parseInt(active.dataset.step, 10);

      const footer = form.querySelector(".forminator-pagination-footer");
      const nextBtn = form.querySelector(".forminator-button-next");
      const backBtn = form.querySelector(".forminator-button-back");

      // Steps 1 e 2 (radio): sem botões → oculta footer inteiro
      if (footer) {
        footer.style.display = step === 1 || step === 2 ? "none" : "";
        footer.style.justifyContent = step === 3 ? "flex-end" : "";
      }
      if (nextBtn)
        nextBtn.style.display = step === 1 || step === 2 ? "none" : "";
      // Step 3 (contato): sem botão "Anterior"
      if (backBtn)
        backBtn.style.display =
          step === 1 || step === 2 || step === 3 ? "none" : "";
    }

    const observer = new MutationObserver(updateNextBtn);
    form.querySelectorAll(".forminator-pagination").forEach((page) => {
      observer.observe(page, {
        attributes: true,
        attributeFilter: ["hidden", "style"],
      });
    });
    updateNextBtn();

    // ── Mensagem de sucesso após envio ────────────────────────────────────────────
    const responseMsg = form.querySelector(".forminator-response-message");
    if (responseMsg) {
      const successObs = new MutationObserver(() => {
        if (responseMsg.classList.contains("forminator-success")) {
          successObs.disconnect();
          form.dataset.submitted = "true";
          // Remove o display:none inline que o Forminator coloca
          responseMsg.style.removeProperty("display");
          // Oculta paginação, footer e botão submit
          form
            .querySelectorAll(
              ".forminator-pagination, .forminator-pagination-footer, .forminator-button-submit",
            )
            .forEach((el) => (el.style.display = "none"));
        }
      });
      successObs.observe(responseMsg, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }
  } // fim setupForm
} // fim initDiagnosticoForm

// ─── Carrossel mobile: Cursos Gratuitos ──────────────────────────────────────
function initCursosGratuitosCarousel() {
  const grid = document.querySelector(".home-cursos-gratuitos__grid");
  if (!grid) {
    return;
  }

  const cards = Array.from(
    grid.querySelectorAll(".home-cursos-gratuitos__card"),
  );

  if (cards.length === 0) {
    return;
  }

  const prevBtn = document.querySelector(".js-cgrat-prev");
  const nextBtn = document.querySelector(".js-cgrat-next");
  let current = 0;

  function isMobile() {
    return window.innerWidth < 1024;
  }

  function update() {
    if (!isMobile()) {
      cards.forEach((c) => c.style.removeProperty("display"));
      return;
    }

    cards.forEach((c, i) => {
      c.style.display = i === current ? "flex" : "none";
    });

    if (prevBtn) {
      prevBtn.disabled = current === 0;
    }

    if (nextBtn) {
      nextBtn.disabled = current >= cards.length - 1;
    }
  }

  prevBtn?.addEventListener("click", () => {
    if (current > 0) {
      current--;
      update();
    }
  });

  nextBtn?.addEventListener("click", () => {
    if (current < cards.length - 1) {
      current++;
      update();
    }
  });

  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(update, 100);
  });

  update();
}

function initPlanosToggle() {
  document.querySelectorAll(".js-planos-toggle").forEach((btn) => {
    const body = btn.closest(".consultoria-planos__card-body");

    if (!body) {
      return;
    }

    const items = body.querySelector(".consultoria-planos__items");

    if (!items) {
      return;
    }

    btn.addEventListener("click", () => {
      const expanded = items.classList.toggle(
        "consultoria-planos__items--expanded",
      );
      const label = btn.querySelector(".js-toggle-label");
      const chevron = btn.querySelector(".js-toggle-chevron svg");

      if (label) {
        label.textContent = expanded
          ? "Ver menos"
          : "Mostrar todos os benefícios";
      }

      if (chevron) {
        chevron.style.transform = expanded ? "rotate(180deg)" : "";
      }
    });
  });
}

// ─── Guia: acordeão de módulos ───────────────────────────────────────────────
function initGuiaAccordion() {
  const items = document.querySelectorAll("[data-accordion-item]");

  if (!items.length) {
    return;
  }

  items.forEach((item) => {
    const btn = item.querySelector(".single-guia__accordion-trigger");

    if (!btn) {
      return;
    }

    btn.addEventListener("click", () => {
      const isOpen = item.classList.contains("is-open");

      items.forEach((i) => {
        i.classList.remove("is-open");
        i.querySelector(".single-guia__accordion-trigger")?.setAttribute(
          "aria-expanded",
          "false",
        );
      });

      if (!isOpen) {
        item.classList.add("is-open");
        btn.setAttribute("aria-expanded", "true");
      }
    });
  });
}

// ─── Guia: vídeo placeholder ─────────────────────────────────────────────────
function initGuiaVideo() {
  document
    .querySelectorAll(".single-guia__video-wrap[data-yt-url]")
    .forEach((wrap) => {
      const btn = wrap.querySelector(".single-guia__video-play");

      if (!btn) {
        return;
      }

      btn.addEventListener(
        "click",
        () => {
          const url = wrap.dataset.ytUrl || "";
          const urlMatch = url.match(
            /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?#]+)/,
          );

          if (!urlMatch?.[1]) {
            return;
          }

          const iframe = document.createElement("iframe");
          iframe.src = `https://www.youtube.com/embed/${urlMatch[1]}?autoplay=1`;
          iframe.setAttribute(
            "allow",
            "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
          );
          iframe.setAttribute("allowfullscreen", "");
          iframe.className = "single-guia__iframe";
          wrap.replaceChildren(iframe);
          wrap.style.cursor = "default";
        },
        { once: true },
      );
    });
}

document.addEventListener("DOMContentLoaded", () => {
  DiagQuiz.init();
  initVideoFacade();
  initFaq();
  initClinicasVideos();
  initDiagnosticoForm();
  initCursosGratuitosCarousel();
  initPlanosToggle();
  initGuiaAccordion();
  initGuiaVideo();
});
